import { FC, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import { useI18n } from "../../hooks/useTranslations";
import { User } from "../../http/users/types";
import { getLanguages, getMeasurmentTypes, Language, MeasurmentType } from "../../types";
import { formatDateNumeric } from "../../types/utils";
import Box from "../ui/Box";
import DatePicker from "../../ui/DatePicker";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import ModalDialog from "../ui/ModalDialog";
import reactSelectStyles from "../ui/ReactSelectUtils";
import styles from "./MyProfileForm.module.css";

export interface MyProfileFormBody {
  measurmentType: MeasurmentType;
  drivingLicenseExpiration: string;
  language: Language;
}

interface MyProfileFormProps {
  user?: Omit<User, "roles" | "permissions"> | null;
  onSubmit: (formData: MyProfileFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
}

const MyProfileForm: FC<MyProfileFormProps> = ({ onSubmit, user, isDisabled, isLoading }: MyProfileFormProps) => {
  const i18n = useI18n();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const emptyValue = i18n.t.common.empty;

  const languages = getLanguages(i18n);
  const distaceOptions = getMeasurmentTypes(i18n);

  const defaultValues = {
    measurmentType: user?.distanceInMiles
      ? distaceOptions.find((x) => x.value === "imperial")
      : distaceOptions.find((x) => x.value === "metric"),
    drivingLicenseExpiration: user?.drivingLicenseExpiration,
    language: languages.find((x) => x.value === user?.preferredLanguage),
  };

  return (
    <>
      <Form<MyProfileFormBody>
        submitText={i18n.t.common.save}
        onSubmit={() => onSubmit}
        defaultValues={defaultValues}
        disabled={isDisabled}
      >
        {({ formState: { errors }, reset, setValue, watch, control }) => {
          useEffect(() => {
            !!user && reset(defaultValues);
          }, [user, i18n.language]);

          return (
            <div className={styles.sections}>
              <Box.Group
                boxs={[
                  <Box
                    fields={[
                      [
                        {
                          title: i18n.t.common.firstName,
                          data: user?.firstName || emptyValue,
                        },
                        {
                          title: i18n.t.common.lastName,
                          data: user?.lastName || emptyValue,
                        },
                      ],
                      [
                        {
                          title: i18n.t.common.birthdate,
                          data: user?.birthDate ? formatDateNumeric(user.birthDate, i18n.language) : emptyValue,
                        },
                        {
                          title: i18n.t.common.costCenter,
                          data: user?.currentCostCenter || emptyValue,
                        },
                      ],
                      [
                        {
                          title: i18n.t.common.email,
                          data: user?.email ? (
                            <a className={styles.link} href={`mailto:${user.email}`}>
                              {user.email}
                            </a>
                          ) : (
                            emptyValue
                          ),
                        },
                        {
                          title: i18n.t.common.department,
                          data: user?.department || emptyValue,
                        },
                      ],
                    ]}
                  />,
                ]}
              />

              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>{i18n.t.common.settings}</h3>
                <FormFieldsContainer>
                  <FormField
                    label={i18n.t.common.drivingLicenseExpirationDate}
                    error={errors.drivingLicenseExpiration}
                    isOptional
                    showHelp={{
                      onOpen: !isLoading && !isDisabled ? () => setIsModalOpen(true) : undefined,
                    }}
                  >
                    {({ isInvalid }) => (
                      <DatePicker
                        isInvalid={isInvalid}
                        value={watch("drivingLicenseExpiration")}
                        onChange={(v) => setValue("drivingLicenseExpiration", v!)}
                        clearable
                        disabled={isDisabled || isLoading}
                      />
                    )}
                  </FormField>

                  <Controller
                    name="measurmentType"
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.t.common.distances} error={fieldState.error} isOptional>
                        {({ labelId, isInvalid }) => (
                          <Select<MeasurmentType>
                            {...field}
                            id={labelId}
                            options={distaceOptions}
                            isLoading={isLoading}
                            isDisabled={isDisabled || isLoading}
                            placeholder={`${i18n.t.common.select}...`}
                            getOptionLabel={(x) => x.label}
                            getOptionValue={(x) => x.value}
                            styles={reactSelectStyles<MeasurmentType>(isInvalid)}
                            menuPosition="fixed"
                          />
                        )}
                      </FormField>
                    )}
                  />
                  <Controller
                    name="language"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <FormField label={i18n.t.common.preferredLanguage} isOptional>
                        <Select<Language>
                          {...field}
                          options={languages}
                          isLoading={isLoading}
                          isDisabled={isDisabled || isLoading}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) => x.label}
                          getOptionValue={(x) => x.value}
                          styles={reactSelectStyles<Language>()}
                          menuPosition="fixed"
                        />
                      </FormField>
                    )}
                  />
                </FormFieldsContainer>
              </div>
            </div>
          );
        }}
      </Form>

      {isModalOpen && (
        <ModalDialog
          isOnlyTextDialog
          onClose={() => setIsModalOpen(false)}
          title={i18n.t.common.drivingLicenseExpirationDate}
          actions={[
            {
              title: i18n.t.common.close,
              onClick: () => setIsModalOpen(false),
            },
          ]}
          isSubmitting={false}
        >
          {i18n.t.common.drivingLicenseText}
        </ModalDialog>
      )}
    </>
  );
};

export default MyProfileForm;
